/*
 * 回忆点滴第一层 —— 右上角日历图标打开的页面。
 *
 * 旧版对应 `packages/components/src/characterUserProjection/
 * UserCharacterMemory.tsx`：一条按日期**倒序**的时间线，每项写
 * 「YYYY-MM-DD - 标题」。
 *
 * ⚠ **点进去不是日记本身，是那一天的对话列表**（`DayPage`），日记退到
 * 那一页顶上的按钮后面。这一层只负责挑哪一天。
 *
 * 这个顺序不是排版偏好：日记是她对那天的复述，对话是那天真正发生的事。
 * 把复述摆在前面，用户翻回忆时先读到的是二手的概括，
 * 而他真正想找的「那天我们聊了什么」还要再点一层。旧版就是这么排的。
 *
 * ⚠ 这一页还曾经被写成「记忆中心 / 她记得的事」，列的是自动提炼的事实条目、
 * 置信度、共享范围，外加一段「记忆不会因为读取失败而丢失」的产品承诺。
 * 那些是新造的东西，旧版这个入口里一个都没有，已按要求改回日记时间线。
 *
 * 数据是 `RelationshipDiary`（缈缈写的、关于你的日记），**不是** `CharacterDiary`
 * ——后者是产品级的缈缈日记，旧版用户端从未暴露，新库还有
 * `CHECK (deidentified = true)` 从结构上禁止它带用户身份。
 *
 * 连不上就只说一句失去联系。不列任何条目，也不解释「东西还在」——
 * 那种解释本身就在暗示可能不在了。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LoadingState, PageNotice } from "../../components/AsyncState";
import { Button, PageShell } from "../../components/ui";
import { IconCalendar, IconRetry } from "../../components/Icons";
import { fetchMemories, type MemoryCenter, type MemoryDiary } from "../../services/me-api";
import { useAppearance } from "../../state/session";
import { useResource } from "../../state/resource";
import "../pages.css";

export function MemoriesPage() {
  const data = useResource<MemoryCenter>((signal) => fetchMemories(signal), []);
  const appearance = useAppearance();

  /*
   * 首屏那一页由 useResource 管，翻出来的后续页面累加在这里。
   * 与 FavoritesPage 同一套写法——用户端四个列表页步长都是 30，
   * 逻辑一致才不会各自长歪。
   */
  const [morePages, setMorePages] = useState<MemoryDiary[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);

  const center = data.state.phase === "ready" ? data.state.data : null;
  const failed = data.state.phase === "failed";
  const recoveryPending = center?.recovery?.pending ?? 0;
  const recoveryPolls = useRef(0);

  useEffect(() => {
    if (!center || recoveryPending === 0) {
      recoveryPolls.current = 0;
      return;
    }
    // 两次模型调用（先摘要、再日记）可能需要几分钟；页面停留期间后台刷新，
    // 不把已经显示的时间线反复切回 loading。上限防止 worker 停用时无限请求。
    if (recoveryPolls.current >= 80) return;
    const timer = window.setTimeout(() => {
      recoveryPolls.current += 1;
      data.refresh();
    }, 3_000);
    return () => window.clearTimeout(timer);
  }, [center, data.refresh, recoveryPending]);

  const diaries: MemoryDiary[] = [...(center?.diaries ?? []), ...morePages];

  /*
   * 还有没有下一页，要用**已加载条数**跟总数比，不能用第一页的 hasMore：
   * 那个值描述的是「第一页之后还有没有」，翻到底之后它仍然是 true。
   */
  const total = center?.diaryTotal ?? 0;
  const hasMore = diaries.length < total;

  const loadMore = useCallback(() => {
    setLoadingMore(true);
    setMoreFailed(false);
    // 游标是「服务端那边已经翻过多少条」。
    fetchMemories(undefined, diaries.length)
      .then((page) => setMorePages((prev) => [...prev, ...page.diaries]))
      .catch(() => setMoreFailed(true))
      .finally(() => setLoadingMore(false));
  }, [diaries.length]);

  return (
    <PageShell
      icon={<IconCalendar size={20} />}
      title="回忆点滴"
      help={
        <>
          <p>
            每天睡前，{appearance.name}都会把当天和你有关的事写下来一小段，
            攒着攒着就成了这里的一串日子啦。
          </p>
          <p>
            点开哪一天，就能看到那天的本地对话和已追回的站外记录。追回内容只陈列，
            不会自动生成心情日记。
          </p>
        </>
      }
      /* 重试只在读不到的时候出现。有数据时摆一个重试键，等于暗示这页可能是坏的。 */
      actions={
        failed ? (
          <Button tone="quiet" icon={<IconRetry size={15} />} onClick={data.reload}>
            重试
          </Button>
        ) : undefined
      }
    >
      {data.state.phase === "loading" ? (
        <LoadingState label="正在翻看回忆" />
      ) : null}

      {center && recoveryPending > 0 ? (
        <p className="inline-hint">发现有几段回忆还没整理好，正在补写，完成后会自动出现。</p>
      ) : null}

      {/* 这里必须写完整判断而不是用上面的 failed：布尔量不参与类型收窄，
          data.state.error 会被当成可能为 null。 */}
      {data.state.phase === "failed" ? (
        <PageNotice
          scene="memory"
          error={data.state.error}
          onRetry={data.reload}
          expected={[
            "按日期倒序的回忆时间线，每天一条",
            "点开某一天：本地对话、站外记录与心情日记",
          ]}
        />
      ) : null}

      {center && diaries.length === 0 && recoveryPending === 0 ? (
        // 旧版空态原文就是这一句。
        <p className="inline-hint">让我们不断创造回忆吧！</p>
      ) : null}

      {diaries.length > 0 ? (
        <div className="timeline">
          {diaries.map((diary) => (
            /*
              用 <Link> 而不是 button + navigate：这些地址会被分享出去，
              长按复制链接、新标签页打开都得是原生行为。
            */
            <Link
              key={diary.id}
              to={`/memories/${encodeURIComponent(diary.date)}`}
              className="timeline__item"
            >
              {/* 旧版就是「日期-标题」连成一行，中间一个连字符。 */}
              <span className="timeline__line">
                {diary.date}
                {diary.title ? `-${diary.title}` : ""}
              </span>
            </Link>
          ))}
        </div>
      ) : null}

      {/* 总天数与已显示条数对不上时说明还有更早的。 */}
      {center && total > diaries.length ? (
        <p className="inline-hint">
          共 {total} 天，已显示 {diaries.length} 天。
        </p>
      ) : null}

      {hasMore ? (
        <Button tone="quiet" onClick={loadMore} disabled={loadingMore}>
          {loadingMore ? "正在翻更早的…" : "看看更早的回忆"}
        </Button>
      ) : null}

      {/* 翻页失败只说这一次没成，不动已经显示出来的那些。 */}
      {moreFailed ? (
        <p className="inline-hint">刚才没翻动，再点一次试试。</p>
      ) : null}
    </PageShell>
  );
}
